import React, { useState, useEffect } from "react";
import Spinner from "../loading/spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  convertTo24Hour,
  formatDateToDDMMYY,
  formatTime,
  getWeekday,
} from "../../specialMethod/getBetweenDate";
import CalendarInput from "./calendarInput";
import { toggleState } from "../../specialMethod/toggleState";
import EditPatient from "./editPatient";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getClinics = () => {
  try {
    return JSON.parse(localStorage.getItem("clinics")) || [];
  } catch {
    return [];
  }
};

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

const getPatients = () => {
  try {
    return JSON.parse(localStorage.getItem("patients")) || [];
  } catch {
    return [];
  }
};

const savePatients = (patients) => {
  localStorage.setItem("patients", JSON.stringify(patients));
};

const generateId = () =>
  "apt_" + Date.now() + "_" + Math.random().toString(36).substring(2, 8);

// ============================================
// MAIN COMPONENT
// ============================================

const NewPatientForm = ({ responseData, resett, patientDetails, reset }) => {
  const [state, setState] = useState({
    loadingSpinner: false,
    selectClinic: [],
    filteredClinics: [],
    timeSlots: [],
    loadingEditPatient: false,
    isChangePatientDataValue: "",
  });

  // For clinic appointment counts (mock)
  const [clinicCounts, setClinicCounts] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());

  const formik = useFormik({
    initialValues: {
      name: patientDetails?.name || "",
      abhaNumber: patientDetails?.abhaNumber || responseData?.abhaNumber || "",
      age: patientDetails?.age || "",
      contact: patientDetails?.contact || responseData?.contact || "",
      gender: patientDetails?.gender || "",
      appointmentDate: formatDateToDDMMYY(new Date()),
      appointmentTime: "",
      clinicId: "",
      clinicLocation: "",
      guardianName: patientDetails?.guardianName || "",
      address: patientDetails?.address || "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      name: Yup.string().required("Name is required"),
      age: Yup.string().required("Age is required"),
      contact: Yup.string()
        .matches(/^\d{10}$/, "Must be 10 digits")
        .required("Contact is required"),
      gender: Yup.string().required("Gender is required"),
      appointmentDate: Yup.string().required("Appointment date is required"),
      appointmentTime: Yup.string().required("Appointment time is required"),
      clinicId: Yup.string().required("Clinic is required"),
      clinicLocation: Yup.string().required("Clinic location is required"),
    }),
    onSubmit: (values) => {
      addPatientData(values);
    },
  });

  // Load clinics from localStorage
  useEffect(() => {
    const clinics = getClinics();
    const activeClinics = clinics.filter((c) => c.clinicStatus !== "DELETED");
    toggleState("selectClinic", activeClinics, setState);
    toggleState("filteredClinics", activeClinics, setState);
    // Compute clinic counts for the selected date
    computeClinicCounts(selectedDate);
  }, []);

  // Compute appointment counts per clinic for a given date
  const computeClinicCounts = (date) => {
    const dateStr = formatDateToDDMMYY(date);
    const appointments = getAppointments();
    const counts = {};
    appointments.forEach((apt) => {
      if (apt.appointmentDate === dateStr) {
        const clinicId = apt.clinicId;
        counts[clinicId] = (counts[clinicId] || 0) + 1;
      }
    });
    setClinicCounts(counts);
  };

  // Update clinic counts when date changes
  useEffect(() => {
    computeClinicCounts(selectedDate);
  }, [selectedDate]);

  // Update appointmentDate in form when selectedDate changes
  useEffect(() => {
    formik.setFieldValue("appointmentDate", formatDateToDDMMYY(selectedDate));
  }, [selectedDate]);

  // Filter clinics based on selected date (weekday)
  useEffect(() => {
    const { appointmentDate } = formik.values;
    if (appointmentDate && state.selectClinic.length > 0) {
      const calculatedWeekday = getWeekday(appointmentDate).toUpperCase();
      const filtered = state.selectClinic.filter((clinic) =>
        clinic.days?.includes(calculatedWeekday)
      );
      toggleState("filteredClinics", filtered, setState);
    }
  }, [formik.values.appointmentDate, state.selectClinic]);

  // Auto-select clinic if only one is filtered
  useEffect(() => {
    if (state.filteredClinics.length === 1) {
      const { id, location } = state.filteredClinics[0];
      formik.setFieldValue("clinicId", id);
      formik.setFieldValue("clinicLocation", location);
    }
  }, [state.filteredClinics]);

  // Generate time slots when clinic location changes
  useEffect(() => {
    const { clinicLocation } = formik.values;
    if (clinicLocation) {
      const clinic = state.selectClinic.find(
        (c) => c.location === clinicLocation
      );
      if (clinic) {
        generateTimeSlots(clinic.startTime, clinic.endTime);
      }
    }
  }, [formik.values.clinicLocation, state.selectClinic]);

  const generateTimeSlots = (startTime, endTime) => {
    const slots = [];
    const start = new Date(`1970-01-01T${startTime}`);
    const end = new Date(`1970-01-01T${endTime}`);
    while (start <= end) {
      slots.push(formatTime(start));
      start.setMinutes(start.getMinutes() + 5);
    }
    toggleState("timeSlots", slots, setState);
  };

  const handleClinicChange = (e) => {
    const clinicId = e.target.value;
    const clinicLocation =
      e.target.selectedOptions[0].getAttribute("data-location");
    formik.setValues({
      ...formik.values,
      clinicId: clinicId,
      clinicLocation: clinicLocation,
    });
  };

  // ============================================
  // PATIENT AND APPOINTMENT CREATION
  // ============================================

  const createPatient = (values) => {
    // Create a new patient record in localStorage
    const patients = getPatients();
    const newPatient = {
      id: "pat_" + Date.now(),
      name: values.name,
      abhaNumber: values.abhaNumber || "",
      age: values.age,
      contact: values.contact,
      gender: values.gender,
      guardianName: values.guardianName || "",
      address: values.address || "",
      createdAt: new Date().toISOString(),
    };
    patients.push(newPatient);
    savePatients(patients);
    return newPatient.id;
  };

  const bookAppointment = (values, patientId) => {
    const appointments = getAppointments();
    const newAppointment = {
      id: generateId(),
      patientId: patientId,
      name: values.name,
      abhaNumber: values.abhaNumber || "",
      age: values.age,
      contact: values.contact,
      gender: values.gender,
      appointmentDate: values.appointmentDate,
      appointmentTime: convertTo24Hour(values.appointmentTime),
      clinicId: parseInt(values.clinicId, 10),
      clinicLocation: values.clinicLocation,
      guardianName: values.guardianName || "",
      address: values.address || "",
      paymentStatus: "UNPAID",
      appointmentvisitStatus: "PENDING",
      createdAt: new Date().toISOString(),
    };
    appointments.push(newAppointment);
    saveAppointments(appointments);
    return newAppointment;
  };

  const getNewPatient = (values, patientId) => {
    toggleState("loadingSpinner", true, setState);
    try {
      const appointment = bookAppointment(values, patientId);
      toast.success("Appointment booked successfully!");
      // Close modals
      if (reset) reset();
      if (resett) resett();
      return appointment;
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      toggleState("loadingSpinner", false, setState);
    }
  };

  const newPatientData = (values) => {
    toggleState("loadingSpinner", true, setState);
    try {
      const patientId = createPatient(values);
      getNewPatient(values, patientId);
    } catch (error) {
      toast.error("Error creating patient.");
      console.error(error);
    } finally {
      toggleState("loadingSpinner", false, setState);
    }
  };

  const addPatientData = (values) => {
    if (!patientDetails) {
      newPatientData(values);
    } else {
      // Check if any field changed
      const hasChanged =
        patientDetails.name !== values.name ||
        patientDetails.age !== values.age ||
        patientDetails.abhaNumber !== values.abhaNumber ||
        patientDetails.contact !== values.contact ||
        patientDetails.gender !== values.gender ||
        patientDetails.guardianName !== values.guardianName ||
        patientDetails.address !== values.address;

      if (hasChanged) {
        toggleState("loadingEditPatient", true, setState);
        toggleState("isChangePatientDataValue", values, setState);
      } else {
        // No changes, directly book appointment with existing patient ID
        getNewPatient(values, patientDetails.id);
        if (reset) reset();
        if (resett) resett();
      }
    }
  };

  const handleResetPatient = () => {
    toggleState("loadingEditPatient", false, setState);
    // After edit, book appointment with updated patient details
    getNewPatient(state.isChangePatientDataValue, patientDetails.id);
    if (reset) reset();
    if (resett) resett();
  };

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-1 right-0 left-0 z-50 h-full items-center justify-center flex"
      >
        <div className="relative p-4 w-full max-w-md h-full md:h-auto">
          <div className="relative bg-white rounded-lg shadow">
            <button
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
              onClick={resett || reset}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close popup</span>
            </button>
            <div className="px-5 pt-5 pb-3">
              <div className="text-center mb-4">
                <p className="mb-2 text-2xl font-semibold leading-5 text-slate-900">
                  Book Appointment
                </p>
              </div>
              {/* Form */}
              <form onSubmit={formik.handleSubmit}>
                <div className="grid grid-cols-2 gap-6 mt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Name <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="name"
                      placeholder="Full Name"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-red-500 text-sm">{formik.errors.name}</div>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="abhaNumber"
                      className="block text-sm font-medium text-gray-700"
                    >
                      ABHA Number
                    </label>
                    <input
                      type="text"
                      name="abhaNumber"
                      value={formik.values.abhaNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="abhaNumber"
                      placeholder="ABHA Number"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.abhaNumber && formik.errors.abhaNumber && (
                      <p className="text-red-500 text-sm">{formik.errors.abhaNumber}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 mt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="age"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Age <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="age"
                      value={formik.values.age}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="age"
                      placeholder="Age"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.age && formik.errors.age && (
                      <p className="text-red-500 text-sm">{formik.errors.age}</p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="contact"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Phone Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      name="contact"
                      value={formik.values.contact}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="contact"
                      placeholder="Mobile Number"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.contact && formik.errors.contact && (
                      <p className="text-red-500 text-sm">{formik.errors.contact}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 mt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="gender"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Gender <span className="text-red-600">*</span>
                    </label>
                    {patientDetails?.gender ? (
                      <input
                        className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                        id="gender"
                        name="gender"
                        value={patientDetails.gender}
                        readOnly
                      />
                    ) : (
                      <select
                        className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                        id="gender"
                        name="gender"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.gender}
                      >
                        <option value="">Select Gender</option>
                        <option value="MALE">Male</option>
                        <option value="FEMALE">Female</option>
                        <option value="OTHER">Other</option>
                      </select>
                    )}
                    {formik.touched.gender && formik.errors.gender && (
                      <p className="text-red-500 text-sm">{formik.errors.gender}</p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="guardianName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Guardian Name
                    </label>
                    <input
                      type="text"
                      name="guardianName"
                      value={formik.values.guardianName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="guardianName"
                      placeholder="Guardian Name"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.guardianName && formik.errors.guardianName && (
                      <p className="text-red-500 text-sm">{formik.errors.guardianName}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 mt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      id="address"
                      placeholder="Address"
                      className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    />
                    {formik.touched.address && formik.errors.address && (
                      <p className="text-red-500 text-sm">{formik.errors.address}</p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <CalendarInput
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6 mt-1">
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="clinic"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Select Clinic <span className="text-red-600">*</span>
                    </label>
                    {state.filteredClinics.length === 1 ? (
                      <div className="py-2 px-4 text-black border border-gray-400 rounded-lg">
                        {state.filteredClinics[0].location} -{" "}
                        {clinicCounts[state.filteredClinics[0].id] || 0}
                        <input
                          type="hidden"
                          name="clinicId"
                          value={formik.values.clinicId}
                        />
                      </div>
                    ) : (
                      <select
                        id="clinic"
                        name="clinicId"
                        value={formik.values.clinicId}
                        onChange={handleClinicChange}
                        onBlur={formik.handleBlur}
                        className="w-full py-2 px-4 text-black border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Select Clinic</option>
                        {state.filteredClinics.map((clinic) => (
                          <option
                            key={clinic.id}
                            value={clinic.id}
                            data-location={clinic.location}
                          >
                            {clinic.location.substring(0, 12)} -{" "}
                            {clinicCounts[clinic.id] || 0}
                          </option>
                        ))}
                      </select>
                    )}
                    {formik.touched.clinicId && formik.errors.clinicId && (
                      <p className="text-red-500 text-sm">{formik.errors.clinicId}</p>
                    )}
                    {state.filteredClinics.length === 0 && (
                      <p className="text-red-500 text-sm">No clinics available on this date</p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label
                      htmlFor="appointmentTime"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Schedule Time <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="appointmentTime"
                      name="appointmentTime"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.appointmentTime}
                      className="w-full py-2 px-4 text-black border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Select Timings</option>
                      {state.timeSlots.map((slot, index) => (
                        <option key={index} value={slot}>
                          {slot}
                        </option>
                      ))}
                    </select>
                    {formik.touched.appointmentTime && formik.errors.appointmentTime && (
                      <p className="text-red-500 text-sm">{formik.errors.appointmentTime}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    type="submit"
                    disabled={state.loadingSpinner}
                    className="w-full bg-green-500 hover:bg-blue-600 text-white font-medium py-2 rounded-lg focus:outline-none disabled:opacity-50"
                  >
                    {state.loadingSpinner ? "Booking..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {state.loadingEditPatient && (
        <EditPatient
          resetval={handleResetPatient}
          closeEdit={() => toggleState("loadingEditPatient", false, setState)}
          patientDetails={patientDetails}
          patientValue={state.isChangePatientDataValue}
          reset={reset}
          resett={resett}
        />
      )}
      <ToastContainer />
      {state.loadingSpinner && <Spinner />}
    </>
  );
};

export default NewPatientForm;